import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Plus, UserPlus, Mail, Phone,
  ChevronRight, X, User, Trash2, ArrowRight,
  FastForward
} from "lucide-react";
import {
  getAllCustomers,
  deleteCustomer,
  createCustomer,
} from "../../services/bookings";
import GlobalLoader from "../ui/GlobalLoader";
import { Customer } from "../../types/customer";
import { useToast } from "../../hooks/useToast";
import { cn } from "../../lib/utils";
import BackButton from "./BackButton";

interface Service {
  id: string;
  name: string;
  duration: string;
  price: string;
}

interface Props {
  setSelectedCustomer: (customer: Customer) => void;
  setStep: (
    step:
      | "selection"
      | "customer"
      | "summary"
      | "payment"
      | "finalPayment"
      | "addCustomer"
  ) => void;
  selectedServices: Service[];
}

const CustomerSelection: React.FC<Props> = ({
  setSelectedCustomer,
  setStep,
  selectedServices,
}) => {
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [customerList, setCustomerList] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all customers initially
  const fetchAllCustomers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllCustomers({ per_page: 50 });
      setCustomerList(response.data.data || []);
    } catch (err: any) {
      const msg = err.message || "Failed to fetch all customers";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilteredCustomers = async (search: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllCustomers({ search, per_page: 50 });
      setCustomerList(response.data.data || []);
    } catch (err: any) {
      const msg = err.message || "Failed to find customers matching search";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCustomers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      fetchAllCustomers();
    } else {
      const timeout = setTimeout(() => fetchFilteredCustomers(searchTerm), 300);
      return () => clearTimeout(timeout);
    }
  }, [searchTerm]);

  const handleSkip = () => {
    const walkInCustomer: Customer = {
      id: 0,
      name: "Walk-in Customer",
      email: "guest@example.com",
    };
    setSelectedCustomer(walkInCustomer);
    if (selectedServices.length > 0) {
      setStep("summary");
    } else {
      setStep("selection");
    }
  };

  const getInitials = (name: string) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2) || "??";
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="flex flex-col h-full bg-[#F9F9F9] -mx-4 sm:-mx-8 -my-4 sm:-my-6">
      {/* List Header */}
      <div className="px-4 sm:px-8 pt-8 pb-6 bg-white border-b border-gray-100 flex flex-col gap-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-1">
          <div>
            <h4 className="text-[17px] sm:text-[18px] font-[600] text-[#402B69] leading-tight">Choose Customer</h4>
            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium mt-0.5">Search or add new customer for this booking</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSkip}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 transition-all font-bold text-[11px] sm:text-xs border border-gray-100"
            >
              <span>Skip</span>
            </button>
            <button
              onClick={() => setStep("addCustomer")}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-[#885ABB]/10 text-[#885ABB] rounded-xl hover:bg-[#885ABB] hover:text-white transition-all duration-300 font-bold text-[11px] sm:text-xs group whitespace-nowrap"
            >
              <Plus size={14} className="group-hover:rotate-90 transition-transform duration-300" />
              <span>Add New</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#885ABB] transition-colors">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Search by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 pl-11 pr-12 bg-[#F9F9F9] border border-gray-100 rounded-xl text-sm font-medium focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] focus:bg-white transition-all outline-none"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Customer List Container */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6">
        {loading && customerList.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center opacity-60">
            <GlobalLoader initialPageLoad={false} description="Loading customers..." compensateSidebar={false} />
          </div>
        ) : error ? (
          <div className="h-[200px] flex flex-col items-center justify-center text-center px-10">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500 mb-3">
              <Mail size={24} />
            </div>
            <p className="text-sm font-semibold text-gray-500">{error}</p>
            <button
              onClick={fetchAllCustomers}
              className="mt-3 text-[#885ABB] font-bold text-xs hover:underline"
            >
              Try again
            </button>
          </div>
        ) : customerList.length === 0 ? (
          <div className="h-[200px] flex flex-col items-center justify-center text-center px-10">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 mb-3">
              <User size={24} />
            </div>
            <p className="text-sm font-semibold text-gray-400">No customers found matching "{searchTerm}"</p>
            <button
              onClick={() => setStep("addCustomer")}
              className="mt-3 text-[#885ABB] font-bold text-xs hover:underline"
            >
              Create new customer
            </button>
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 gap-3"
          >
            {customerList.map((customer) => (
              <motion.div
                key={customer.id}
                variants={itemVariants}
                onClick={() => {
                  setSelectedCustomer(customer);
                  if (selectedServices.length > 0) {
                    setStep("summary");
                  } else {
                    setStep("selection");
                  }
                }}
                className="group relative flex items-center justify-between p-4 bg-white border border-gray-100 rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-md hover:border-[#885ABB]/30"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#885ABB]/5 flex items-center justify-center text-[#885ABB] text-sm font-bold border border-[#885ABB]/10 group-hover:bg-[#885ABB] group-hover:text-white transition-all duration-300">
                    {getInitials(customer.name)}
                  </div>
                  <div>
                    <h5 className="text-[15px] font-[600] text-[#402B69] group-hover:text-[#885ABB] transition-colors">{customer.name}</h5>
                    <div className="flex items-center gap-3 text-[11px] text-gray-400 font-medium mt-0.5">
                      {customer.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={10} /> <span>{customer.phone}</span>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-1">
                          <Mail size={10} /> <span>{customer.email.length > 20 ? customer.email.substring(0, 20) + '...' : customer.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-[#885ABB]/10 group-hover:text-[#885ABB] transition-all">
                  <ChevronRight size={18} />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

    </div>
  );
};

export default CustomerSelection;
